import {
  useContext,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { EmbedContext } from "./EmbedFrame.js";
import {
  canEditDirective,
  spliceDirectiveAttrs,
  unwrapDirective,
  type DirectiveNode,
} from "./parseDirective.js";
import { EDIT_SLOT_ID } from "../layout/RightSidebar.js";

const MAX_AUTO_COLS = 4;

/** Column count: explicit `cols` attr wins, else one per child (capped). */
function colCount(attrs: Record<string, string>, childCount: number): number {
  const explicit = Number.parseInt(attrs.cols ?? "", 10);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return Math.max(1, Math.min(childCount, MAX_AUTO_COLS));
}

interface SidebysideProps {
  node: DirectiveNode;
  attrs: Record<string, string>;
  /** Each direct child of the directive, pre-rendered into one column cell. */
  columns: ReactNode[];
}

/**
 * Renders a `:::sidebyside` directive: its block children laid out in a
 * responsive grid. The embeds *inside* stay individually selectable/editable —
 * the grid inherits the page's `EmbedContext`, so clicking an interior embed
 * selects/edits that embed via its own frame. The sidebyside frame itself only
 * edits the directive's *attributes* (e.g. `cols`); restructuring the row's
 * contents is done in the raw markdown.
 */
export function Sidebyside({ node, attrs, columns }: SidebysideProps) {
  const ctx = useContext(EmbedContext);
  const [editing, setEditing] = useState(false);

  const cols = colCount(attrs, columns.length);
  const grid = (
    <div className="dw-sidebyside" style={{ "--dw-cols": cols } as CSSProperties}>
      {columns}
    </div>
  );

  if (!ctx.interactive) return grid;

  const index = ctx.nodeIndex.get(node) ?? -1;
  const selected = ctx.selected === index;
  const editable = canEditDirective(ctx.source, node);

  const updateAttrs = (next: Record<string, string>): void => {
    ctx.onSourceChange(spliceDirectiveAttrs(ctx.source, node, next));
  };

  // Remove only the layout wrapper — the embeds inside stay, back to stacking
  // full-width. This is a directive-only affordance: a code embed has no such
  // "unwrap" since it isn't a wrapper around anything.
  const unwrapRow = (): void => {
    setEditing(false);
    ctx.onSourceChange(unwrapDirective(ctx.source, node));
  };

  const slot =
    typeof document !== "undefined"
      ? document.getElementById(EDIT_SLOT_ID)
      : null;

  return (
    <div
      className={`dw-embed-frame${selected ? " dw-selected" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        ctx.setSelected(selected ? null : index);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (editable) setEditing(true);
      }}
    >
      <span className="dw-embed-tag">
        sidebyside{!editable && " · indented (edit raw)"}
      </span>
      {grid}

      {selected &&
        slot &&
        createPortal(
          <section className="dw-card dw-embed-details">
            <header className="dw-card-head">sidebyside</header>
            <div className="dw-card-body">
              <table className="dw-attr-table">
                <tbody>
                  <tr>
                    <td className="dw-muted">columns</td>
                    <td>{cols}</td>
                  </tr>
                  {Object.entries(attrs).map(([k, v]) => (
                    <tr key={k}>
                      <td className="dw-muted">{k}</td>
                      <td>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {editable ? (
                <button onClick={() => setEditing(true)}>edit attrs</button>
              ) : (
                <p className="dw-muted">
                  Indented/quoted directive — edit in the raw editor.
                </p>
              )}
              <p className="dw-muted">
                Click an embed inside the row to edit it. Add/remove/reorder
                embeds in the raw markdown.
              </p>
            </div>
          </section>,
          slot,
        )}

      {editing &&
        createPortal(
          <div className="dw-modal-backdrop" onClick={() => setEditing(false)}>
            <div className="dw-modal" onClick={(e) => e.stopPropagation()}>
              <header className="dw-modal-head">
                <span>sidebyside · attributes</span>
                <button className="dw-link" onClick={() => setEditing(false)}>
                  done
                </button>
              </header>
              <AttrEditor attrs={attrs} onChange={updateAttrs} />
              <div className="dw-modal-foot">
                <button className="dw-link dw-danger-link" onClick={unwrapRow}>
                  remove layout (keep embeds)
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/**
 * Attribute-only editor: a key/value table, no body textarea. Each change
 * commits the whole attr set back through onChange (which splices just the
 * directive's `{…}`).
 */
function AttrEditor({
  attrs,
  onChange,
}: {
  attrs: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const [pairs, setPairs] = useState<[string, string][]>(() =>
    Object.entries(attrs),
  );

  const commit = (next: [string, string][]): void => {
    const obj: Record<string, string> = {};
    for (const [k, v] of next) if (k) obj[k] = v;
    onChange(obj);
  };

  const setPair = (i: number, k: string, v: string): void => {
    const next = pairs.map((p, j) =>
      j === i ? ([k, v] as [string, string]) : p,
    );
    setPairs(next);
    commit(next);
  };

  const addPair = (): void => setPairs([...pairs, ["", ""]]);

  const removePair = (i: number): void => {
    const next = pairs.filter((_, j) => j !== i);
    setPairs(next);
    commit(next);
  };

  return (
    <div className="dw-fence-editor">
      <table className="dw-attr-table">
        <tbody>
          {pairs.map(([k, v], i) => (
            <tr key={i}>
              <td>
                <input
                  className="dw-attr-key"
                  value={k}
                  placeholder="key"
                  spellCheck={false}
                  onChange={(e) => setPair(i, e.target.value, v)}
                />
              </td>
              <td>
                <input
                  className="dw-attr-val"
                  value={v}
                  placeholder="value"
                  spellCheck={false}
                  onChange={(e) => setPair(i, k, e.target.value)}
                />
              </td>
              <td>
                <button className="dw-link" onClick={() => removePair(i)}>
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="dw-link" onClick={addPair}>
        + attr
      </button>
    </div>
  );
}
